import React, { useState, useMemo, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useSearchParams, useNavigate } from "react-router-dom";
import "./style.css";
import SearchInput from "../../../components/common/SearchInputField";
import ButtonMain from "../../../components/common/ButtonMain";
import TicketAllUser from "../../../components/dashboardcomponents/TicketSectionComponents/TicketMainSection/TicketAllTable";
import CreateTicket from "../../../components/dashboardcomponents/TicketSectionComponents/CreateTicket/CreateTicket";
import TickeetChatSection from "../../../components/dashboardcomponents/TicketSectionComponents/TicketChatSection/TickeetChatSection";
import { ReactComponent as BACKBUTTON } from "../../../assets/images/backbutton.svg";
import {
  getAllTickets,
  addTicketOptimistic,
  updateTicketOptimistic,
  removeTicketOptimistic,
  adjustStatusCountsForNewTicket,
  adjustStatusCountsForStatusChange,
  buildTicketListParams,
} from "../../../store/features/getAllTickets";
import TableSkeleton from "../../../components/dashboardcomponents/TicketSectionComponents/TicketMainSection/TicketListSkeleton";
import TicketExportButton from "../../../components/dashboardcomponents/TicketSectionComponents/TicketExportButton";
import { useSocket } from "../../../SocketContext";
import useDebounce from "../../../hooks/useDebounce";
import { isHeaderInlinePageTitleEnabled } from "../../../config/featureFlags";


const TicketManagmentTable = [0, 1, 2, 3];
const statusMap = {
  1: 'open',
  2: 'pending',
  3: 'solved',
  4: 'spam'
};

const reverseStatusMap = {
  'open': 1,
  'pending': 2,
  'solved': 3,
  'spam': 4
};

function TicketManagment() {
  const dispatch = useDispatch();
  const socket = useSocket();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(() => {
    const tabParam = searchParams.get('tab');
    return tabParam ? reverseStatusMap[tabParam] || 0 : 0;
  });
  const [selectedState, setSelectedState] = useState(() => {
    const ticketId = searchParams.get('ticketId');
    return ticketId ? TicketManagmentTable[2] : TicketManagmentTable[0];
  });
  const [searchQuery, setSearchQuery] = useState("");

  const {
    tickets: allTickets,
    loading: allTicketsLoading,
    statusCounts,
  } = useSelector((state) => state.tickets);

  const currentLanguage = useSelector((state) => state.language.currentLanguage);

  const t = useSelector((state) => state.language.translations[currentLanguage]);

 
  const ticketCounts = useMemo(() => {
    const sc = statusCounts || {};
    const sumByStatus =
      (sc.open || 0) + (sc.pending || 0) + (sc.solved || 0) + (sc.spam || 0);
    return {
      all: sc.all > 0 ? sc.all : sumByStatus,
      open: sc.open || 0,
      pending: sc.pending || 0,
      solved: sc.solved || 0,
      spam: sc.spam || 0,
    };
  }, [statusCounts]);

  // Get parameters from URL
  const sortOrder = searchParams.get('sortOrder') || 'desc';
  const rawSortField = searchParams.get('sortField') || 'updatedAt';
  const sortField =
    rawSortField === 'updated_at' ? 'updatedAt' : rawSortField;
  const page = parseInt(searchParams.get('page') || '0', 10);

  const navigate = useNavigate();

  const searchKey = searchParams.toString();

  // Search runs server-side: push the term into the URL so the list refetch
  // (keyed on searchKey) queries every record, not just the loaded page.
  const debouncedSearch = useDebounce(searchQuery, 400);
  const prevDebouncedSearchRef = useRef(debouncedSearch);
  useEffect(() => {
    if (prevDebouncedSearchRef.current === debouncedSearch) return;
    prevDebouncedSearchRef.current = debouncedSearch;
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      const term = debouncedSearch.trim();
      if (term) {
        params.set('search', term);
      } else {
        params.delete('search');
      }
      params.set('page', '0');
      return params;
    }, { replace: true });
  }, [debouncedSearch, setSearchParams]);

  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam && reverseStatusMap[tabParam] !== undefined) {
      setActiveTab(reverseStatusMap[tabParam]);
    }
  }, [searchParams]);

  useEffect(() => {
    const ticketId = searchParams.get('ticketId');
    if (ticketId && selectedState !== TicketManagmentTable[2]) {
      setSelectedState(TicketManagmentTable[2]);
      return;
    }
    if (!ticketId && selectedState === TicketManagmentTable[2]) {
      setSelectedState(TicketManagmentTable[0]);
    }
  }, [searchParams, selectedState]);

  useEffect(() => {
    const tabParam = searchParams.get('tab');
    const tabIdx =
      tabParam && reverseStatusMap[tabParam] !== undefined
        ? reverseStatusMap[tabParam]
        : 0;
    dispatch(getAllTickets(buildTicketListParams(tabIdx, searchParams)));
  }, [dispatch, searchKey]);

  useEffect(() => {
    const onFocus = () => {
      if (socket && !socket.connected) {
        console.log('Reconnecting socket on tab focus...');
        socket.connect();
      }
      const tabParam = searchParams.get('tab');
      const tabIdx =
        tabParam && reverseStatusMap[tabParam] !== undefined
          ? reverseStatusMap[tabParam]
          : 0;
      dispatch(
        getAllTickets({
          ...buildTicketListParams(tabIdx, searchParams),
          silent: true,
        })
      );
    };

    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [dispatch, searchKey, socket, searchParams]);

  const handleTabClick = (tabName) => {
    setSearchQuery("");
    prevDebouncedSearchRef.current = "";
    setActiveTab(tabName);
    const params = new URLSearchParams(searchParams);
    params.delete('search');
    const status = statusMap[tabName];
    if (status) {
      params.set('tab', status);
    } else {
      params.delete('tab');
    }
    params.set('page', '0');
    setSearchParams(params);
  };

  useEffect(() => {
    if (!socket) return;

    const refetchList = () => {
      const tabParam = searchParams.get('tab');
      const tabIdx =
        tabParam && reverseStatusMap[tabParam] !== undefined
          ? reverseStatusMap[tabParam]
          : 0;
      dispatch(
        getAllTickets({
          ...buildTicketListParams(tabIdx, searchParams),
          silent: true,
        })
      );
    };

    const tabIndexFromUrl = () => {
      const tabParam = searchParams.get('tab');
      if (tabParam && reverseStatusMap[tabParam] !== undefined) {
        return reverseStatusMap[tabParam];
      }
      return 0;
    };

    const handleTicketCreated = (data) => {
      console.log('Ticket created event received:', data);
      if (!data?.ticket) return;

      const newTicket = data.ticket;
      const ticketStatus = newTicket.status?.toLowerCase();
      const tabIdx = tabIndexFromUrl();

      dispatch(adjustStatusCountsForNewTicket({ status: ticketStatus }));

      if (tabIdx === 0) {
        dispatch(addTicketOptimistic(newTicket));
      } else {
        const currentStatusTab = statusMap[tabIdx];
        if (currentStatusTab && ticketStatus === currentStatusTab) {
          dispatch(addTicketOptimistic(newTicket));
        }
      }
      refetchList();
    };

    const handleTicketStatusChanged = (data) => {
      console.log('Ticket status changed event received:', data);

      const oldStatus = data?.oldStatus?.toLowerCase?.() ?? '';
      const newStatus = data?.newStatus?.toLowerCase?.() ?? '';
      const ticket = data?.ticket;
      const ticketId = ticket?._id ?? data?.ticketId;

      if (oldStatus && newStatus && oldStatus !== newStatus) {
        dispatch(adjustStatusCountsForStatusChange({ oldStatus, newStatus }));
      }

      const tabIdx = tabIndexFromUrl();
      const currentStatusTab = tabIdx !== 0 ? statusMap[tabIdx] : null;

      if (tabIdx === 0) {
        if (ticket) {
          dispatch(updateTicketOptimistic(ticket));
        }
      } else if (currentStatusTab) {
        if (ticket) {
          if (oldStatus === newStatus) {
            dispatch(updateTicketOptimistic(ticket));
          } else if (oldStatus === currentStatusTab && newStatus !== currentStatusTab) {
            dispatch(removeTicketOptimistic(ticket._id));
          } else if (newStatus === currentStatusTab) {
            dispatch(addTicketOptimistic(ticket));
          } else {
            dispatch(removeTicketOptimistic(ticket._id));
          }
        } else if (ticketId && oldStatus === currentStatusTab && newStatus !== currentStatusTab) {
          dispatch(removeTicketOptimistic(ticketId));
        }
      }

      refetchList();
    };

    socket.on('ticket-created', handleTicketCreated);
    socket.on('ticket-status-changed', handleTicketStatusChanged);

    return () => {
      socket.off('ticket-created', handleTicketCreated);
      socket.off('ticket-status-changed', handleTicketStatusChanged);
    };
  }, [socket, dispatch, searchKey, searchParams]);

  const handleGoBack = () => {
    setSelectedState(0);
    const params = new URLSearchParams(searchParams);
    params.delete('ticketId');
    navigate(`/ticketsection?${params.toString()}`);

    const tabParam = params.get('tab');
    const tabForFetch = tabParam && reverseStatusMap[tabParam] !== undefined
      ? reverseStatusMap[tabParam]
      : 0;
    dispatch(getAllTickets(buildTicketListParams(tabForFetch, params)));
  };

  const isTabActive = (tabName) => activeTab === tabName;

  const displayedTickets = allTickets;
  const showListSkeleton = allTicketsLoading && allTickets.length === 0;

  const registrationUIRender = useMemo(
    () => (
      <>
        {selectedState === 0 ? (
          <div className="Ticket-Main-Table-Container">
            {showListSkeleton ? (
              <TableSkeleton rows={8} />
            ) : (
              <TicketAllUser
                setSelectedState={setSelectedState}
                tickets={displayedTickets}
                loading={showListSkeleton}
                searchQuery={searchQuery}
                initialSortOrder={sortOrder}
                initialSortField={sortField}
                initialPage={page}
                activeTab={activeTab}
              />
            )}
          </div>
        ) : selectedState === 1 ? (
          <>
            <div className="go-back" onClick={() => handleGoBack()}>
              <BACKBUTTON />
              <div>
                <p className="create-ticket-heading ">Create New Ticket</p>
              </div>
            </div>
            <div className="Ticket-Main-Container">
              <CreateTicket setSelectedState={setSelectedState} />
            </div>
          </>
        ) : selectedState === 2 ? (
          <div className="Team-Main-Container">
            <TickeetChatSection setSelectedState={setSelectedState} />
          </div>
        ) : null}
      </>
    ),
    [selectedState, displayedTickets, showListSkeleton, searchQuery, sortOrder, sortField, page, activeTab]
  );

  const containerClassName = selectedState === 1 || selectedState === 2 ? "CreateTicketContainer" : "TicketManagment-main-container";

  return (
    <div className={containerClassName}>
      {selectedState === 1 || selectedState === 2 ? null : (
        <div className="TicketManagment-Container001">
          <div
            className="Ticket-title-row"
            style={
              isHeaderInlinePageTitleEnabled
                ? { justifyContent: "flex-end" }
                : undefined
            }
          >
            {/* Legacy in-page title — hidden when header shows inline title (Application V2) */}
            {!isHeaderInlinePageTitleEnabled && (
              <p className="Ticket-text">{t.TICKETTEXT}</p>
            )}
            <TicketExportButton
              buttonLabel={t.EXPORTTICKETS}
              jsonLabel={t.EXPORTTICKETSJSON}
              csvLabel={t.EXPORTTICKETSCSV}
            />
          </div>
          <div className="Ticket-main-header">
            <div className="Ticket-text-section">
              <p
                className={`ticket-header-text ${isTabActive(0) ? "TicketHeaderActive" : ""}`}
                onClick={() => {
                  handleTabClick(0);
                  setSelectedState(0);
                }}
              >
                {t.ALLTICKETS} ({ticketCounts.all})
              </p>
              <p
                className={`ticket-header-text ${isTabActive(1) ? "TicketHeaderActive" : ""}`}
                onClick={() => {
                  handleTabClick(1);
                }}
              >
                {t.TICKETOPEN} ({ticketCounts.open})
              </p>

              <p
                className={`ticket-header-text ${isTabActive(2) ? "TicketHeaderActive" : ""}`}
                onClick={() => {
                  handleTabClick(2);
                }}
              >
                {t.TICKETPENDING} ({ticketCounts.pending})
              </p>
              <p
                className={`ticket-header-text ${isTabActive(3) ? "TicketHeaderActive" : ""}`}
                onClick={() => {
                  handleTabClick(3);
                }}
              >
                {t.TICKETSOLVED} ({ticketCounts.solved})
              </p>
              <p
                className={`ticket-header-text ${isTabActive(4) ? "TicketHeaderActive" : ""}`}
                onClick={() => {
                  handleTabClick(4);
                }}
              >
                {t.TICKETSPAM} ({ticketCounts.spam})
              </p>
            </div>
            <div style={{ marginBottom: '24px' }} className="inviteButton-container">
              <SearchInput
                title=""
                placeholder="Search by customer name, email, ID, or tag"
                className="SearchInput-teamsection"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <ButtonMain
                className="inviteButton"
                onClick={() => setSelectedState(1)}
                title={t.CREATETICKET}
                showIcon={true}
              />
            </div>
          </div>
        </div>
      )}
      {registrationUIRender}
    </div>
  );
}

export default TicketManagment;
